import { DialogType } from '../../../@types';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import useFilterForm from '../../../hooks/useFilterForm';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormSelect from '../../molecules/FormSelect';
import ValidityPeriodSelect from '../../molecules/ValidityPeriodSelect';
import { Dayjs } from 'dayjs';
import {
  AFFILIATE_CAMPAIGN_PRODUCT_OPTIONS,
  AFFILIATE_CAMPAIGN_STATUS_OPTIONS,
  AFFILIATE_CAMPAIGN_TYPE_OPTIONS,
} from '../../../constants';
import type {
  Partner,
  AffiliateCampaignType,
  AffiliateCampaignStatus,
  AffiliateCampaignProduct,
  ValidityPeriod,
} from '../../../@types/api';
import PartnerSelect from '../../molecules/PartnerSelect';
import { QUERY_KEYS } from '../../../constants';
import { USER_ROLES } from '../../../constants/keycloak';
import usePermissions from '../../../hooks/usePermissions';

export const getAffiliateOptionLabel = (options: Partner[], value: string) => {
  const affiliate = options.find((option) => option.userId === value);

  if (!affiliate) return '';

  return affiliate.email ? `${affiliate.fullName} (${affiliate.email})` : affiliate.fullName;
};

const AFFILIATE_QUERY_PARAMS = {
  roleTypes: [USER_ROLES.AFFILIATE],
} as const;

type CampaignsFilterFormData = {
  agentId?: string | null;
  type?: AffiliateCampaignType;
  product?: AffiliateCampaignProduct;
  status?: AffiliateCampaignStatus;
  validityPeriod?: ValidityPeriod;
  fromTimestamp?: Dayjs | null;
  toTimestamp?: Dayjs | null;
};

const DEFAULT_FORM_DATA: CampaignsFilterFormData = {
  agentId: undefined,
  type: undefined,
  product: undefined,
  status: undefined,
  validityPeriod: 'ONGOING',
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterCampaigns = ({ closeModal, changeQuery }: Props) => {
  const { isAdmin, isSuperAdmin } = usePermissions();
  const { control, handleSubmit, handleResetFilter } = useFilterForm<CampaignsFilterFormData>({
    defaultData: DEFAULT_FORM_DATA,
    changeQuery,
  });

  const handleClose = () => {
    closeModal?.();
  };

  return (
    <FilterDialogLayout label='campaigns' onClose={handleClose} onSave={handleSubmit} onReset={handleResetFilter}>
      {(isAdmin || isSuperAdmin) && (
        <PartnerSelect
          name='agentId'
          control={control}
          label='Affiliate'
          queryKey={[QUERY_KEYS.partners, 'campaign-affiliate-filter']}
          queryParams={AFFILIATE_QUERY_PARAMS}
          getOptionLabel={getAffiliateOptionLabel}
          multipleSelect={false}
        />
      )}
      <FormSelect control={control} name='status' label='Status' options={AFFILIATE_CAMPAIGN_STATUS_OPTIONS} />
      <FormSelect control={control} name='type' label='Campaign Type' options={AFFILIATE_CAMPAIGN_TYPE_OPTIONS} />
      <FormSelect control={control} name='product' label='Product' options={AFFILIATE_CAMPAIGN_PRODUCT_OPTIONS} />
      <ValidityPeriodSelect control={control} disabled />
    </FilterDialogLayout>
  );
};

export default FilterCampaigns;
